import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {map, catchError, switchMap, tap} from 'rxjs/operators';
import { AdvancedSearchService } from '../../services/advanced-search.service';
import * as AdvancedSearchActions from './advanced-search.actions';
import * as SubscriptionActions from '../subscription/subscription.actions';
import {Router} from "@angular/router";

@Injectable()
export class AdvancedSearchEffects {

  searchCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedSearchActions.searchCompanies),
      switchMap(({ filters, page, pageSize, nextPageToken }) => {
        return this.searchService.searchCompanies(filters, page, pageSize, nextPageToken).pipe(
          map((response) => {
            return AdvancedSearchActions.searchCompaniesSuccess({ response });
          }),
          catchError((error) => {
            console.error('Search companies error:', error);
            return of(AdvancedSearchActions.searchCompaniesFailure({ error }));
          })
        );
      })
    )
  );

  // A Veridion company search deducts 1 credit per call (search + each page).
  // Re-fetch the authoritative balance once the search settles so the sidebar
  // credit counter stays in sync. We refresh on failure too, because the backend
  // deducts before calling Veridion, so a credit can be spent even on error.
  refreshCreditsAfterSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AdvancedSearchActions.searchCompaniesSuccess,
        AdvancedSearchActions.searchCompaniesFailure
      ),
      map(() => SubscriptionActions.loadCredits())
    )
  );

  navigateAfterSearch$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AdvancedSearchActions.searchCompaniesSuccess),
        tap(() => this.router.navigate(['/advanced-search/companies']))
      ),
    { dispatch: false }
  );

  searchPeople$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedSearchActions.searchPeople),
      switchMap((action) => {
        const params = (action as any).params;
        // Use by-domain API when website_domain is available, otherwise fall back to fuzzy search
        const search$ = params.website_domain
          ? this.searchService.searchPeopleByDomain(params)
          : this.searchService.searchPeople(params);
        return search$.pipe(
          map((response) => AdvancedSearchActions.searchPeopleSuccess({ response })),
          catchError((error) => of(AdvancedSearchActions.searchPeopleFailure({ error })))
        );
      })
    )
  );

  // NEW Location Effects - Store-based approach
  loadCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedSearchActions.loadCountries),
      switchMap(() =>
        this.searchService.searchCountries('').pipe(
          map((response) => {
            const countries = response?.data?.countries || [];
            return AdvancedSearchActions.loadCountriesSuccess({
              countries: countries.map((country, index) => ({
                id: index + 1,
                name: country.name,
                value: country.id
              }))
            });
          }),
          catchError((error) => of(AdvancedSearchActions.loadCountriesFailure({ error })))
        )
      )
    )
  );

  loadRegions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedSearchActions.loadRegions),
      switchMap(({ countryId, countryName, veridionCountryId }) =>
        this.searchService.searchRegions(countryName, veridionCountryId, '').pipe(
          map((response) => {
            const regions = response?.data?.regions || [];
            return AdvancedSearchActions.loadRegionsSuccess({
              countryId,
              regions: regions.map((region, index) => ({
                id: index + 1,
                name: region.name,
                value: region.id
              }))
            });
          }),
          catchError((error) => of(AdvancedSearchActions.loadRegionsFailure({ error })))
        )
      )
    )
  );

  loadCities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedSearchActions.loadCities),
      switchMap(({ regionId, countryName, regionName, veridionCountryId, veridionRegionId }) =>
        this.searchService.searchCities(countryName, regionName, veridionCountryId, veridionRegionId, '').pipe(
          map((response) => {
            const cities = response?.data?.cities || [];
            return AdvancedSearchActions.loadCitiesSuccess({
              regionId,
              cities: cities.map((city, index) => ({
                id: index + 1,
                name: city.name,
                value: city.id
              }))
            });
          }),
          catchError((error) => of(AdvancedSearchActions.loadCitiesFailure({ error })))
        )
      )
    )
  );

  // Company Industries
  searchCompanyIndustries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedSearchActions.searchCompanyIndustries),
      switchMap(({ searchTerm }) =>
        this.searchService.searchCompanyIndustries(searchTerm).pipe(
          map((response) => {
            return AdvancedSearchActions.searchCompanyIndustriesSuccess({ response });
          }),
          catchError((error) => {
            console.error('Error in searchCompanyIndustries effect:', error);
            return of(AdvancedSearchActions.searchCompanyIndustriesFailure({ error }));
          })
        )
      )
    )
  );

  // Legacy effects (for backward compatibility)
  searchCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedSearchActions.searchCountries),
      switchMap(({ searchTerm }) =>
        this.searchService.searchCountries(searchTerm).pipe(
          map((response) => {
            return AdvancedSearchActions.searchCountriesSuccess({ response });
          }),
          catchError((error) => {
            console.error('Error in searchCountries effect:', error);
            return of(AdvancedSearchActions.searchCountriesFailure({ error }));
          })
        )
      )
    )
  );

  searchRegions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedSearchActions.searchRegions),
      switchMap(({ countryName, veridionCountryId, searchTerm }) =>
        this.searchService.searchRegions(countryName, veridionCountryId, searchTerm).pipe(
          map((response) => AdvancedSearchActions.searchRegionsSuccess({ response })),
          catchError((error) => of(AdvancedSearchActions.searchRegionsFailure({ error })))
        )
      )
    )
  );

  searchCities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedSearchActions.searchCities),
      switchMap(({ countryName, regionName, veridionCountryId, veridionRegionId, searchTerm }) =>
        this.searchService.searchCities(countryName, regionName, veridionCountryId, veridionRegionId, searchTerm).pipe(
          map((response) => AdvancedSearchActions.searchCitiesSuccess({ response })),
          catchError((error) => of(AdvancedSearchActions.searchCitiesFailure({ error })))
        )
      )
    )
  );

  searchLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedSearchActions.searchLocations),
      switchMap(({ country, region, city }) => {
        return this.searchService.searchCountries(country).pipe(
          map((response) => AdvancedSearchActions.searchLocationsSuccess({ response })),
          catchError((error) => of(AdvancedSearchActions.searchLocationsFailure({ error })))
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private searchService: AdvancedSearchService,
    private router: Router
  ) {}
}
